import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  CreditCard,
  FlaskConical,
  Home,
  Layers,
  Radio,
  Settings as SettingsIcon,
  Sparkles,
  Ticket,
  Users,
  X,
} from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badge?: string | number;
  end?: boolean;
}

const primaryNav: NavItem[] = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/cohorts", label: "Cohorts", icon: Layers, badge: 3 },
  { to: "/programs", label: "Programs", icon: Sparkles, badge: 2 },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/codes", label: "Invite codes", icon: Ticket },
  { to: "/outreach", label: "Outreach", icon: Radio },
  { to: "/sandbox", label: "Sandbox", icon: FlaskConical },
];

const secondaryNav: NavItem[] = [
  { to: "/team", label: "Team", icon: Users },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

function NavItemRow({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group flex items-center justify-between gap-2 rounded-[8px] px-2.5 py-2",
          "text-[14px] font-medium",
          "transition-colors duration-200 ease-out",
          isActive
            ? "bg-[var(--color-graphite-700)] text-[var(--color-snow)] shadow-ring"
            : "text-[var(--color-slate-200)] hover:bg-[var(--color-graphite-700)]/60 hover:text-[var(--color-snow)]",
        )
      }
    >
      <span className="inline-flex items-center gap-2.5">
        <Icon size={16} className="text-current opacity-80" />
        {item.label}
      </span>
      {item.badge !== undefined && (
        <span className="font-mono text-[11px] text-[var(--color-slate-300)]">
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <div className="flex flex-col gap-0.5">
        {primaryNav.map((item) => (
          <NavItemRow key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </div>

      <div
        aria-hidden
        className="h-px bg-[var(--color-graphite-500)]/30 mx-2"
      />

      <div className="flex flex-col gap-0.5">
        {secondaryNav.map((item) => (
          <NavItemRow key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </div>

      <div className="mt-auto px-2 pb-1">
        <p className="font-mono text-[10px] tracking-[0.06em] text-[var(--color-slate-300)] uppercase">
          v0.1.0
        </p>
      </div>
    </>
  );
}

/** Desktop persistent sidebar. */
export function Sidebar() {
  return (
    <aside
      className="hidden md:flex shrink-0 flex-col gap-4 px-3 py-4 border-r border-[var(--color-graphite-500)]/40"
      style={{
        width: "var(--sidebar-width)",
        background: "var(--color-deep-charcoal)",
      }}
    >
      <SidebarContent />
    </aside>
  );
}

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
}

/** Mobile slide-in drawer. Sibling of Sidebar — only one is visible per
 * viewport thanks to the `md:` breakpoint guard. */
export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const location = useLocation();

  // Auto-close on navigation.
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ESC closes, body scroll locks.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={cn(
        "md:hidden fixed inset-0 z-40 transition-opacity duration-200",
        open ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(4,5,6,0.78)]"
        style={{ backdropFilter: "blur(8px)" }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "absolute left-0 top-0 h-full w-[280px] flex flex-col gap-4 px-3 py-4",
          "border-r border-[var(--color-graphite-500)]/40 shadow-xl-eon",
          "transition-transform duration-300 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
        )}
        style={{ background: "var(--color-deep-charcoal)" }}
      >
        <div className="flex items-center justify-between px-2 py-1">
          <Logo />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="h-8 w-8 rounded-[var(--radius-button)] text-[var(--color-slate-300)] hover:bg-[var(--color-graphite-700)] hover:text-[var(--color-snow)] transition-colors inline-flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>
        <SidebarContent onNavigate={onClose} />
      </aside>
    </div>
  );
}
